(function () {
  if (window.customElements && window.customElements.get('pack-selector')) return;

  class PackSelector extends HTMLElement {
    connectedCallback() {
      // A/B test group, set on <html> by snippets/pack-selector.liquid. No group = no test.
      const group = document.documentElement.getAttribute('data-packs-ab');
      if (group) this.saveTestGroup(group);
      if (group === 'off') return;

      this.form = document.getElementById(this.dataset.formId || '');
      this.cards = Array.from(this.querySelectorAll('[data-pack-card]'));
      this.inputs = {
        pack: this.querySelector('[data-pack-input="pack"]'),
        image: this.querySelector('[data-pack-input="image"]'),
        label: this.querySelector('[data-pack-input="label"]'),
      };

      this.cards.forEach((card) => {
        card.addEventListener('click', () => this.select(card));
        card.addEventListener('keydown', (event) => this.onKeydown(event, card));
      });

      const initial = this.cards.find((card) => card.getAttribute('aria-checked') === 'true') || this.cards[0];
      if (initial) this.select(initial, true);
    }

    onKeydown(event, card) {
      const step = { ArrowLeft: 1, ArrowDown: 1, ArrowRight: -1, ArrowUp: -1 }[event.key];
      if (!step) return;
      event.preventDefault();
      const index = (this.cards.indexOf(card) + step + this.cards.length) % this.cards.length;
      this.select(this.cards[index]);
      this.cards[index].focus();
    }

    select(card, silent) {
      const qty = parseInt(card.dataset.qty, 10) || 1;

      this.cards.forEach((c) => {
        const active = c === card;
        c.setAttribute('aria-checked', String(active));
        c.tabIndex = active ? 0 : -1;
      });

      this.setQuantity(qty);
      this.setProperties(qty, card);
      this.setButtonPrice(card.dataset.total);
      this.setTopPrice(card, qty);
      this.setCartBar(card, qty);

      if (!silent) {
        this.dispatchEvent(new CustomEvent('pack-selector:change', { bubbles: true, detail: { quantity: qty } }));
      }
    }

    setQuantity(qty) {
      if (!this.form) return;
      const named = this.form.elements.namedItem('quantity');
      const inputs = named instanceof Element ? [named] : Array.from(named || []);
      inputs.forEach((input) => {
        input.value = String(qty);
      });
    }

    setProperties(qty, card) {
      const isPack = qty > 1;
      const values = {
        pack: String(qty),
        image: card.dataset.imagePath || '',
        label: card.dataset.label || '',
      };

      Object.entries(this.inputs).forEach(([key, input]) => {
        if (!input) return;
        input.value = values[key];
        input.disabled = !isPack || values[key] === '';
      });
    }

    // Keeps the page's price row (custom code block "top_price_section") in line with the
    // selected pack, including the price per 100 ml that must be shown by law.
    setTopPrice(card, qty) {
      const section = (this.closest('[data-section-id]') || document).querySelector('.top_price_section');
      if (!section) return;
      const price = section.querySelector('.top_price_section_price');
      const per100 = section.querySelector('.price_for_100ml b');

      if (price) {
        if (this.originalPrice == null) this.originalPrice = price.innerHTML;
        if (qty === 1) {
          price.innerHTML = this.originalPrice;
        } else {
          price.textContent = '';
          const now = document.createElement('span');
          now.className = 'product-grid-item__price-new';
          now.textContent = card.dataset.total;
          price.appendChild(now);
          if (card.dataset.compare) {
            const was = document.createElement('s');
            was.textContent = card.dataset.compare;
            price.appendChild(was);
          }
        }
      }

      if (per100) {
        if (this.originalPer100 == null) this.originalPer100 = per100.textContent;
        per100.textContent = qty === 1 || !card.dataset.per100 ? this.originalPer100 : card.dataset.per100;
      }
    }

    // The sticky add-to-cart bar (snippets/cart-bar.liquid) submits this same form, so it
    // only needs to show the selected pack: a badge and the pack price.
    setCartBar(card, qty) {
      const bar = (this.closest('product-info') || document).querySelector('cart-bar.has-packs');
      if (!bar) return;

      const badge = bar.querySelector('[data-cart-bar-pack-badge]');
      if (badge) {
        badge.textContent = card.dataset.label || '';
        badge.hidden = qty === 1;
      }

      const price = bar.querySelector('[data-product-price]');
      if (price) price.textContent = card.dataset.total;

      let was = bar.querySelector('.product__price--strike');
      if (!was && card.dataset.compare && price) {
        was = document.createElement('s');
        was.className = 'product__price--strike';
        price.after(was);
      }
      if (was) {
        was.textContent = card.dataset.compare || '';
        was.hidden = !card.dataset.compare;
      }
      if (price) price.classList.toggle('product__price--sale', Boolean(card.dataset.compare));
    }

    // Saves the A/B group and the split it was drawn under on the cart (hidden attributes, copied
    // to the order) once per cart, and tells Google Tag Manager.
    saveTestGroup(group) {
      if (PackSelector.groupSaved) return;
      PackSelector.groupSaved = true;
      const split = document.documentElement.getAttribute('data-packs-split') || '';

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'packs_ab', packs_ab_group: group, packs_ab_split: split });

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          const saved = cart.attributes || {};
          if (saved._packs_ab === group && (saved._packs_split || '') === split) return;
          return fetch(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { _packs_ab: group, _packs_split: split } }),
          });
        })
        .catch(() => {});
    }

    setButtonPrice(total) {
      if (!this.form || !total) return;
      const button = this.form.querySelector('[data-add-to-cart]');
      if (!button) return;

      let price = button.querySelector('[data-pack-atc-price]');
      if (!price) {
        price = document.createElement('span');
        price.className = 'pack-selector__atc-price';
        price.setAttribute('data-pack-atc-price', '');
        const text = button.querySelector('[data-add-to-cart-text]') || button;
        text.appendChild(price);
      }
      price.textContent = ' · ' + total;
    }
  }

  window.customElements.define('pack-selector', PackSelector);
})();
